import {useState} from 'react';

import {RRule} from 'rrule';

import {RecurrenceMode, type RecurrenceRuleInput} from 'nutshell-graphql-types';

import {Stack} from '../../../layout/stack/stack';
import {Popover} from '../../../popover/popover';
import {PopoverForm} from '../../../popover-form/popover-form';
import {Radio} from '../../../radio/radio';
import {RadioGroup} from '../../../radio/radio-group';
import {Body} from '../../../typography/body';

import {getRecurrenceRule} from '../helpers';
import type {RecurrenceRule} from '../task-recurrence-rule';

import {DAY_UNIT, WEEK_UNIT, MONTH_UNIT} from './consts';
import {RecurrenceCompletionModeForm} from './recurrence-completion-mode-form';
import {RecurrenceDueDateModeForm} from './recurrence-due-date-mode-form';

export type CycleUnit = typeof WEEK_UNIT | typeof MONTH_UNIT | typeof DAY_UNIT;

const DEFAULT_REPEAT_VALUES = {cycleUnit: WEEK_UNIT as CycleUnit, cycleValue: 1};

interface Props {
    anchor: HTMLElement | null | undefined;
    onClose: () => void;
    onRecurrenceRuleChange: (recurrenceRule: RecurrenceRuleInput | null) => void;
    recurrenceRule: RecurrenceRule | null;
    referenceDatetime: number | null | undefined;
}

export function TaskCustomRecurrencePopover(props: Props) {
    const [mode, setMode] = useState<RecurrenceMode>(
        props.recurrenceRule?.mode || RecurrenceMode.DUE_DATE
    );

    const rule = props.recurrenceRule ? RRule.fromString(props.recurrenceRule.rule) : null;

    const initialCompletionValues = (() => {
        if (props.recurrenceRule?.mode !== RecurrenceMode.COMPLETION || !rule) {
            return DEFAULT_REPEAT_VALUES;
        }

        return {
            cycleUnit:
                rule.options.freq === RRule.MONTHLY
                    ? (MONTH_UNIT as CycleUnit)
                    : (WEEK_UNIT as CycleUnit),
            cycleValue: rule.options.interval,
        };
    })();

    const initialDueDateValues = (() => {
        if (props.recurrenceRule?.mode !== RecurrenceMode.DUE_DATE || !rule) {
            // TODO: Default to the reference datetime's weekday
            // TODO: Default to the reference datetime's occurrence
            return {...DEFAULT_REPEAT_VALUES, weekday: RRule.MO.weekday, occurrence: 1};
        }

        return {
            cycleUnit:
                rule.options.freq === RRule.MONTHLY
                    ? (MONTH_UNIT as CycleUnit)
                    : (WEEK_UNIT as CycleUnit),
            cycleValue: rule.options.interval,
            weekday:
                rule.options.freq === RRule.MONTHLY
                    ? rule.options.bynweekday?.[0]?.[0] ?? RRule.MO.weekday
                    : rule.options.byweekday?.[0] ?? RRule.MO.weekday,
            occurrence: rule.options.bynweekday?.[0]?.[1] ?? 1,
        };
    })();

    const [dueDateCycleUnit, setDueDateCycleUnit] = useState<CycleUnit>(
        initialDueDateValues.cycleUnit
    );
    const [dueDateCycleValue, setDueDateCycleValue] = useState<number>(
        initialDueDateValues.cycleValue
    );
    const [dueDateWeekday, setDueDateWeekday] = useState<number>(initialDueDateValues.weekday);
    const [dueDateOccurrence, setDueDateOccurrence] = useState<number>(
        initialDueDateValues.occurrence
    );

    const [completionCycleUnit, setCompletionCycleUnit] = useState<CycleUnit>(
        initialCompletionValues.cycleUnit
    );
    const [completionCycleValue, setCompletionCycleValue] = useState<number>(
        initialCompletionValues.cycleValue
    );

    return (
        <Popover
            anchor={props.anchor}
            location='bottom-right'
            onBlur={props.onClose}
            disableOverlayClickToBlur={true}
        >
            <PopoverForm
                title='Custom recurrence'
                subTitle='When should the next task be due?'
                onClose={props.onClose}
                onSubmit={(e) => {
                    e.stopPropagation();

                    props.onRecurrenceRuleChange({
                        mode,
                        rule: getRecurrenceRule(
                            mode,
                            props.referenceDatetime,
                            completionCycleValue,
                            completionCycleUnit,
                            dueDateCycleValue,
                            dueDateCycleUnit,
                            dueDateWeekday,
                            dueDateOccurrence
                        ),
                    });
                }}
            >
                <div className='pb-16'>
                    <RadioGroup
                        name='custom-recurrence-mode'
                        selectedValue={mode}
                        spacing={16}
                        onChange={(value) => {
                            setMode(value as RecurrenceMode);
                        }}
                    >
                        <Stack spacing={8}>
                            <Radio value={RecurrenceMode.DUE_DATE} shouldTopAlignInput={true}>
                                <Body isBold={true} size='small'>
                                    On a set schedule
                                </Body>
                            </Radio>
                            <RecurrenceDueDateModeForm
                                mode={mode}
                                values={{
                                    cycleUnit: dueDateCycleUnit,
                                    cycleValue: dueDateCycleValue,
                                    weekday: dueDateWeekday,
                                    occurrence: dueDateOccurrence,
                                }}
                                setters={{
                                    setCycleUnit: setDueDateCycleUnit,
                                    setCycleValue: setDueDateCycleValue,
                                    setWeekday: setDueDateWeekday,
                                    setOccurrence: setDueDateOccurrence,
                                }}
                            />
                        </Stack>
                        <Stack spacing={8}>
                            <Radio value={RecurrenceMode.COMPLETION} shouldTopAlignInput={true}>
                                <Body isBold={true} size='small'>
                                    Based on when I complete the task
                                </Body>
                            </Radio>
                            <RecurrenceCompletionModeForm
                                mode={mode}
                                values={{
                                    cycleUnit: completionCycleUnit,
                                    cycleValue: completionCycleValue,
                                }}
                                setters={{
                                    setCycleUnit: setCompletionCycleUnit,
                                    setCycleValue: setCompletionCycleValue,
                                }}
                            />
                        </Stack>
                    </RadioGroup>
                </div>
            </PopoverForm>
        </Popover>
    );
}
